import { useContext, useMemo } from "react";
import styled from "styled-components";
import { FiEdit2, FiTrash2 } from "react-icons/fi";

import { formatarData } from "../../Funcoes/FormatarData";
import { formatValorMoney } from '../../Funcoes/FormatValorMoney';
import { VerifyPermission } from "../../Funcoes/VerifyPermission";
import { AuthContext } from "../../Auth/AuthProvider";
import { useModal } from "../../contexts/ModalContext";

import Tabela from '../../Componentes/Utils/Tabela';
import ModalEditFolga from "./ModalEditFolga";
import ModalDeleteFolga from "./ModalDeleteFolga";

const Container = styled.div`
    width: 100%;
    background-color: #EBEBEB;
    border-radius: 15px;
    box-sizing: border-box;
    overflow: hidden;
    margin-top: 10px;
`
const Cabecalho = styled.div`
    width: 100%;
    height: 50px;
    background-color: #dbdbdb;
    box-sizing: border-box;
    display: flex;
    padding: 0 15px;
    align-items: center;
    justify-content: space-between;
`
const Titulo = styled.h2`
    margin: 0;
    font-size: 14px;
    font-weight: bold;
`
const Corpo = styled.div`
    width: 100%;
    box-sizing: border-box;
    display: flex;
    padding: 10px;
`
const TableCell = styled.td`
    text-align: ${(props) => {
        if (props.align === "left") return "left !important";
        if (props.align === "center") return "center !important";
        if (props.align === "right") return "right !important";
        return "left !important";
    }};
`
const AcoesCelula = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
`

const BotaoAcao = styled.button`
    all: unset;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 6px;
    cursor: pointer;
    transition: background-color 0.15s ease, border-color 0.15s ease;

    color: ${(p) => (p.$danger ? "#b91c1c" : "#1d4ed8")};
    background-color: ${(p) =>
        p.$danger ? "rgba(185, 28, 28, 0.12)" : "rgba(29, 78, 216, 0.10)"};
    border: 1px solid transparent;

    &:hover {
        background-color: ${(p) =>
            p.$danger ? "rgba(185, 28, 28, 0.22)" : "rgba(29, 78, 216, 0.20)"};
        border-color: ${(p) =>
            p.$danger ? "rgba(185, 28, 28, 0.5)" : "rgba(29, 78, 216, 0.45)"};
    }

    &:focus-visible {
        outline: 2px solid ${(p) => (p.$danger ? "#b91c1c" : "#1d4ed8")};
        outline-offset: 2px;
    }
`

const ContainerFolgaCampo = ({ folgasCampo, fetchData, protheus }) => {

    const { user } = useContext(AuthContext);
    const { isModalOpen, openModal } = useModal();

    const podeEditar = VerifyPermission(user, {
        modulo: "rh",
        submodulo: "colaborador",
        funcao: "edit_last_folga"
    });

    const podeApagar = VerifyPermission(user, {
        modulo: "rh",
        submodulo: "colaborador",
        funcao: "delete_last_folga"
    });

    const mostrarAcoes = podeEditar || podeApagar;

    const ultimaFolgaEditavel = useMemo(() => {
        if (!folgasCampo || folgasCampo.length === 0) return null;

        const ordenadas = [...folgasCampo].sort((a, b) => {
            if (a.data === b.data) return (b.id || 0) - (a.id || 0);
            return (b.data || '').localeCompare(a.data || '');
        });

        const ultima = ordenadas[0];
        const now = new Date();
        const hoje = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

        return ultima?.data && ultima.data > hoje ? ultima : null;
    }, [folgasCampo]);

    const abrirEditFolga = () => {
        openModal('editLastFolga');
    }

    const abrirDeleteFolga = () => {
        openModal('deleteLastFolga');
    }

    return (
        <>
            <Container>
                <Cabecalho>
                    <Titulo>FOLGAS DE CAMPO</Titulo>
                </Cabecalho>
                <Corpo>
                    <Tabela>
                        <thead>
                            <tr>
                                <th>VALOR</th>
                                <th>DATA</th>
                                <th>RETORNO</th>
                                <th>DIAS GOZO</th>
                                {mostrarAcoes && <th style={{ width: 90 }}></th>}
                            </tr>
                        </thead>
                        <tbody>
                            {
                                folgasCampo?.length > 0 ? folgasCampo.map(item => {
                                    const isUltimaEditavel = ultimaFolgaEditavel && ultimaFolgaEditavel.id === item.id;

                                    return (
                                        <tr key={item.id}>
                                            <TableCell align="left">{"R$ " + (item?.valor ? formatValorMoney(item.valor) : formatValorMoney("0.00"))}</TableCell>
                                            <TableCell align="center">{item?.data ? formatarData(item.data) : '-'}</TableCell>
                                            <TableCell align="center">{item?.data_retorno ? formatarData(item.data_retorno) : '-'}</TableCell>
                                            <TableCell align="center">{item.dias_gozo}</TableCell>
                                            {mostrarAcoes && (
                                                <TableCell align="center">
                                                    {isUltimaEditavel && (
                                                        <AcoesCelula>
                                                            {podeEditar && (
                                                                <BotaoAcao type="button" onClick={abrirEditFolga} title="Editar">
                                                                    <FiEdit2 size={14} />
                                                                </BotaoAcao>
                                                            )}
                                                            {podeApagar && (
                                                                <BotaoAcao type="button" $danger onClick={abrirDeleteFolga} title="Excluir">
                                                                    <FiTrash2 size={14} />
                                                                </BotaoAcao>
                                                            )}
                                                        </AcoesCelula>
                                                    )}
                                                </TableCell>
                                            )}
                                        </tr>
                                    );
                                }) : (
                                    <tr>
                                        <TableCell align="center" colSpan={mostrarAcoes ? 5 : 4}>
                                            COLABORADOR NÃO POSSUÍ FOLGAS DE CAMPO
                                        </TableCell>
                                    </tr>
                                )
                            }
                        </tbody>
                    </Tabela>
                </Corpo>
            </Container>
            {isModalOpen('editLastFolga') && ultimaFolgaEditavel && podeEditar && (
                <ModalEditFolga
                    folga={ultimaFolgaEditavel}
                    fetchData={fetchData}
                    protheus={protheus}
                />
            )}
            {isModalOpen('deleteLastFolga') && ultimaFolgaEditavel && podeApagar && (
                <ModalDeleteFolga
                    folga={ultimaFolgaEditavel}
                    fetchData={fetchData}
                />
            )}
        </>
    )
}

export default ContainerFolgaCampo;
